import React, { useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { AiOutlineDownload } from 'react-icons/ai'
import { HiOutlineShare } from 'react-icons/hi'
import { geoMercator, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'
import countries110m from 'world-atlas/countries-110m.json'
import {
  FiAlertCircle,
  FiCalendar,
  FiGlobe,
  FiMonitor,
  FiSend,
  FiSmartphone,
  FiX,
} from 'react-icons/fi'

import DetectDevice from '../components/DeviceDetection'
import MenuWrap from '../components/BurgerMenu/MenuWrap'
import SideMenu from '../layouts/SideMenu'
import SimpleHeader from '../layouts/SimpleHeader'
import Robot from '../assets/robot.png'
import {
  MY_CAMPAIGNS_CLICK_STATS_QUERY,
  MY_CAMPAIGNS_CLICKS_QUERY,
  MY_CAMPAIGNS_CONVERSION_STATS_QUERY,
  MY_CAMPAIGNS_DETAILS_QUERY,
} from '../services/GraphQLQueries'
import { do_download_string } from '../services/downloads'
import '../layouts/stylesheets/loader-style.css'

const COLORS = {
  bg: '#F9F9F8',
  card: '#ffffff',
  border: '#E4E4E7',
  ink: '#1A1A1A',
  muted: '#71717A',
  verified: '#168A4A',
  verifiedTint: '#ECFDF5',
  unmatched: '#5F6B76',
  unmatchedTint: '#E5E5E5',
  duplicate: '#CBD5E1',
  bot: '#D33125',
  botTint: '#FEE2E2',
  mapNeutral: '#EEF1F0',
  mapActive: '#C9D8CE',
  active: '#1A1A1A',
}

const TREND_GROUP_BY = 'day'
const EMPTY_DATE_RANGE = { start: '', end: '' }

const WORLD_COUNTRIES = feature(countries110m, countries110m.objects.countries).features

const COUNTRY_ID_BY_NAME = {
  argentina: '032',
  australia: '036',
  austria: '040',
  belgium: '056',
  brazil: '076',
  canada: '124',
  chile: '152',
  china: '156',
  colombia: '170',
  denmark: '208',
  egypt: '818',
  france: '250',
  germany: '276',
  greece: '300',
  india: '356',
  indonesia: '360',
  ireland: '372',
  italy: '380',
  japan: '392',
  mexico: '484',
  netherlands: '528',
  norway: '578',
  poland: '616',
  portugal: '620',
  russia: '643',
  'russian federation': '643',
  singapore: '702',
  'south africa': '710',
  spain: '724',
  sweden: '752',
  switzerland: '756',
  thailand: '764',
  turkey: '792',
  'united kingdom': '826',
  uk: '826',
  'great britain': '826',
  england: '826',
  'united states': '840',
  'united states of america': '840',
  usa: '840',
  us: '840',
}

const COUNTRY_ID_BY_CODE = {
  AR: '032',
  AU: '036',
  AT: '040',
  BE: '056',
  BR: '076',
  CA: '124',
  CL: '152',
  CN: '156',
  CO: '170',
  DE: '276',
  DK: '208',
  EG: '818',
  ES: '724',
  FR: '250',
  GB: '826',
  GR: '300',
  IE: '372',
  IN: '356',
  ID: '360',
  IT: '380',
  JP: '392',
  MX: '484',
  NL: '528',
  NO: '578',
  PL: '616',
  PT: '620',
  RU: '643',
  SE: '752',
  SG: '702',
  TH: '764',
  TR: '792',
  UK: '826',
  US: '840',
  ZA: '710',
}

const COUNTRY_ID_BY_CITY = {
  austin: '840',
  berlin: '276',
  chicago: '840',
  dallas: '840',
  frankfurt: '276',
  london: '826',
  'los angeles': '840',
  miami: '840',
  'new york': '840',
  ottawa: '124',
  paris: '250',
  'san francisco': '840',
  seattle: '840',
  sydney: '036',
  tokyo: '392',
  toronto: '124',
  vancouver: '124',
}

const formatNumber = (value) =>
  new Intl.NumberFormat('en-US').format(Number(value || 0))

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value || 0))

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

const getStatPositionTime = (value) => {
  if (value === null || value === undefined) return Number.NaN
  if (typeof value === 'number') return Number.isFinite(value) ? value : Number.NaN

  const raw = String(value).trim()
  if (!raw) return Number.NaN

  const numeric = Number(raw)
  if (Number.isFinite(numeric)) return numeric

  const localDateMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  )
  if (localDateMatch) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = localDateMatch
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ).getTime()
  }

  const parsed = new Date(raw).getTime()
  return Number.isNaN(parsed) ? Number.NaN : parsed
}

const formatShortDate = (value) =>
  new Date(getStatPositionTime(value)).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
  })

const toDateInputValue = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const startOfDateInput = (value) =>
  value ? new Date(`${value}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY

const endOfDateInput = (value) =>
  value ? new Date(`${value}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY

const getDateRangeBounds = (data = []) => {
  const dates = data
    .map((point) => getStatPositionTime(point.position))
    .filter((value) => Number.isFinite(value))

  if (dates.length === 0) return EMPTY_DATE_RANGE

  return {
    start: toDateInputValue(Math.min(...dates)),
    end: toDateInputValue(Math.max(...dates)),
  }
}

const getLast30DayRange = () => {
  const endDate = new Date()
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - 29)
  startDate.setHours(0, 0, 0, 0)

  return {
    start: toDateInputValue(startDate.getTime()),
    end: toDateInputValue(endDate.getTime()),
  }
}

const filterStatsByDateRange = (data = [], range = EMPTY_DATE_RANGE) => {
  const start = startOfDateInput(range.start)
  const end = endOfDateInput(range.end)

  return data.filter((point) => {
    const position = getStatPositionTime(point.position)
    return Number.isFinite(position) && position >= start && position <= end
  })
}

const normalizeCountryId = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return null

  if (/^\d+$/.test(raw)) {
    return raw.padStart(3, '0')
  }

  const upper = raw.toUpperCase()
  if (COUNTRY_ID_BY_CODE[upper]) return COUNTRY_ID_BY_CODE[upper]

  return COUNTRY_ID_BY_NAME[raw.toLowerCase()] || null
}

const getCountryIdFromCity = (city) => {
  const normalized = String(city || '').toLowerCase()
  const knownCity = Object.keys(COUNTRY_ID_BY_CITY).find((key) =>
    normalized.includes(key)
  )
  return knownCity ? COUNTRY_ID_BY_CITY[knownCity] : null
}

const getCountryIdFromClick = (click) =>
  normalizeCountryId(click?.country) || getCountryIdFromCity(click?.city)

const getCountryIdFromLocation = (location) =>
  normalizeCountryId(location?.country) || getCountryIdFromCity(location?.city)
const getCountryLabel = (id) =>
  (Object.entries(COUNTRY_ID_BY_NAME).find(([, countryId]) => countryId === id)?.[0] || id)
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const getNiceMax = (value) => {
  const padded = Math.max(1, value * 1.2)
  const exponent = Math.pow(10, Math.floor(Math.log10(padded)))
  const fraction = padded / exponent
  const niceFraction =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10

  return niceFraction * exponent
}

const distributeConversionsAcrossTrend = (trend, conversionTotal) => {
  if (!conversionTotal || trend.length === 0) return trend

  const clickTotal = trend.reduce((sum, point) => sum + Number(point.clicks || 0), 0)
  if (!clickTotal) return trend

  const weighted = trend.map((point, index) => {
    const exact = (Number(point.clicks || 0) / clickTotal) * conversionTotal
    return {
      ...point,
      conversions: Math.floor(exact),
      conversionFraction: exact % 1,
      originalIndex: index,
    }
  })

  let assigned = weighted.reduce((sum, point) => sum + point.conversions, 0)
  weighted
    .slice()
    .sort((a, b) => b.conversionFraction - a.conversionFraction)
    .forEach((point) => {
      if (assigned >= conversionTotal) return
      if (point.conversions >= point.clicks) return
      point.conversions += 1
      assigned += 1
    })

  return weighted
    .sort((a, b) => a.originalIndex - b.originalIndex)
    .map(({ conversionFraction, originalIndex, ...point }) => point)
}

const distributeBotSignalsAcrossTrend = (trend, botTotal) => {
  if (!botTotal || trend.length === 0) {
    return trend.map((point) => ({ ...point, botSignals: point.botSignals || 0 }))
  }

  const clickTotal = trend.reduce((sum, point) => sum + Number(point.clicks || 0), 0)
  if (!clickTotal) {
    return trend.map((point) => ({ ...point, botSignals: point.botSignals || 0 }))
  }

  const weighted = trend.map((point, index) => {
    const clicks = Number(point.clicks || 0)
    const conversions = Number(point.conversions || 0)
    const capacity = Math.max(clicks - conversions, 0)
    const exact = (clicks / clickTotal) * botTotal
    return {
      ...point,
      botSignals: Math.min(Math.floor(exact), capacity),
      botFraction: exact % 1,
      botCapacity: capacity,
      originalIndex: index,
    }
  })

  let assigned = weighted.reduce((sum, point) => sum + point.botSignals, 0)
  weighted
    .slice()
    .sort((a, b) => b.botFraction - a.botFraction)
    .forEach((point) => {
      if (assigned >= botTotal) return
      if (point.botSignals >= point.botCapacity) return
      point.botSignals += 1
      assigned += 1
    })

  return weighted
    .sort((a, b) => a.originalIndex - b.originalIndex)
    .map(({ botFraction, botCapacity, originalIndex, ...point }) => point)
}

const BOT_USER_AGENT_PATTERNS = [
  { name: 'Googlebot', pattern: /googlebot(?:\/([^\s;,)]+))?/i },
  { name: 'Googlebot Image', pattern: /googlebot-image(?:\/([^\s;,)]+))?/i },
  { name: 'Bingbot', pattern: /bingbot(?:\/([^\s;,)]+))?/i },
  { name: 'DuckDuckBot', pattern: /duckduckbot(?:\/([^\s;,)]+))?/i },
  { name: 'Baiduspider', pattern: /baiduspider(?:\/([^\s;,)]+))?/i },
  { name: 'YandexBot', pattern: /yandexbot(?:\/([^\s;,)]+))?/i },
  { name: 'Applebot', pattern: /applebot(?:\/([^\s;,)]+))?/i },
  { name: 'Facebook Crawler', pattern: /facebookexternalhit(?:\/([^\s;,)]+))?/i },
  { name: 'Twitterbot', pattern: /twitterbot(?:\/([^\s;,)]+))?/i },
  { name: 'LinkedInBot', pattern: /linkedinbot(?:\/([^\s;,)]+))?/i },
  { name: 'AhrefsBot', pattern: /ahrefsbot(?:\/([^\s;,)]+))?/i },
  { name: 'SemrushBot', pattern: /semrushbot(?:\/([^\s;,)]+))?/i },
  { name: 'MJ12bot', pattern: /mj12bot(?:\/([^\s;,)]+))?/i },
  { name: 'DotBot', pattern: /dotbot(?:\/([^\s;,)]+))?/i },
  { name: 'PetalBot', pattern: /petalbot(?:\/([^\s;,)]+))?/i },
  { name: 'GPTBot', pattern: /gptbot(?:\/([^\s;,)]+))?/i },
  { name: 'ClaudeBot', pattern: /claudebot(?:\/([^\s;,)]+))?/i },
  { name: 'PerplexityBot', pattern: /perplexitybot(?:\/([^\s;,)]+))?/i },
  { name: 'CCBot', pattern: /ccbot(?:\/([^\s;,)]+))?/i },
]

const BROWSER_USER_AGENT_PATTERNS = [
  { name: 'Microsoft Edge', pattern: /(?:edg|edge)\/([^\s;,)]+)/i },
  { name: 'Opera', pattern: /(?:opr|opera)\/([^\s;,)]+)/i },
  { name: 'Chrome iOS', pattern: /crios\/([^\s;,)]+)/i },
  { name: 'Firefox iOS', pattern: /fxios\/([^\s;,)]+)/i },
  { name: 'Samsung Internet', pattern: /samsungbrowser\/([^\s;,)]+)/i },
  { name: 'Chrome', pattern: /chrome\/([^\s;,)]+)/i },
  { name: 'Firefox', pattern: /firefox\/([^\s;,)]+)/i },
  { name: 'Safari', pattern: /version\/([^\s;,)]+).*safari/i },
  { name: 'Internet Explorer', pattern: /(?:msie\s|rv:)([^\s;,)]+)/i },
]

const formatUserAgentInfo = (userAgent) => {
  const raw = String(userAgent || '').trim()
  if (!raw) return 'Unknown browser'

  const knownBot = BOT_USER_AGENT_PATTERNS
    .map(({ name, pattern }) => {
      const match = raw.match(pattern)
      return match ? { name, version: match[1] } : null
    })
    .find(Boolean)

  if (knownBot) {
    return knownBot.version ? `${knownBot.name} ${knownBot.version}` : knownBot.name
  }

  const genericBot = raw.match(/([a-z0-9._-]*(?:bot|crawler|spider|slurp)[a-z0-9._-]*)(?:\/([^\s;,)]+))?/i)
  if (genericBot) {
    return genericBot[2] ? `${genericBot[1]} ${genericBot[2]}` : genericBot[1]
  }

  const browser = BROWSER_USER_AGENT_PATTERNS
    .map(({ name, pattern }) => {
      const match = raw.match(pattern)
      return match ? { name, version: match[1] } : null
    })
    .find(Boolean)

  if (browser) {
    return browser.version ? `${browser.name} ${browser.version}` : browser.name
  }

  return 'Unknown browser'
}

const normalizeClicks = (clicks = []) =>
  clicks.map((click) => ({
    ...click,
    rawCreatedAt: click.createdAt,
    createdAtLabel: formatDate(click.createdAt),
    deviceLabel: DetectDevice(click.useragent || '').name,
    userAgentLabel: formatUserAgentInfo(click.useragent),
    referrerLabel: click.referer || '-',
    userAgentTooltip: click.useragent || 'No user agent recorded',
    locationLabel:
      [click.city, click.region, click.country].filter(Boolean).join(', ') ||
      'Unknown',
  }))

const normalizeCves = (cves = []) =>
  cves.map((cve) => ({
    ...cve,
    rawCreatedAt: cve.submittedAtUtc,
    createdAtLabel: formatDate(cve.submittedAtUtc),
    deviceLabel: cve.useragent ? DetectDevice(cve.useragent).name : '-',
    userAgentLabel: cve.useragent ? formatUserAgentInfo(cve.useragent) : 'Unmatched',
    referrerLabel: cve.referrer || cve.referer || '-',
    userAgentTooltip: cve.useragent || 'No user agent recorded',
    locationLabel:
      [cve.city, cve.region, cve.country].filter(Boolean).join(', ') ||
      'Unknown',
    ip: cve.ip || '-',
    status: cve.status || 'Unknown',
  }))

const mergeStats = (clickStats = [], conversionStats = []) => {
  const map = new Map()
  clickStats.forEach((item) => {
    const key = getStatPositionTime(item.position)
    if (!Number.isFinite(key)) return
    map.set(key, { position: key, clickCount: item.clickCount || 0, conversionCount: 0 })
  })
  conversionStats.forEach((item) => {
    const key = getStatPositionTime(item.position)
    if (!Number.isFinite(key)) return
    const current = map.get(key) || { position: key, clickCount: 0, conversionCount: 0 }
    current.conversionCount = item.conversionCount || 0
    map.set(key, current)
  })
  return Array.from(map.values()).sort((a, b) => a.position - b.position)
}

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="mb-6 flex items-end justify-between gap-4 border-b border-[#e4e4e7] pb-4">
    <div className="flex flex-col gap-1">
      <h2 className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1a1a]/90">
        {title}
      </h2>
      {subtitle ? (
        <p className="text-[13px] font-light leading-relaxed tracking-wide text-[#71717a]">
          {subtitle}
        </p>
      ) : null}
    </div>
    {action}
  </div>
)

const StatItem = ({ label, value, subValue, colorType, emphasis }) => {
  const accent = {
    verified: COLORS.verified,
    bot: COLORS.bot,
    unmatched: COLORS.unmatched,
    duplicate: '#cbd5e1',
  }[colorType]

  return (
    <div className={`relative flex flex-col gap-1.5 ${emphasis ? 'opacity-100' : 'opacity-80'}`}>
      {accent ? (
        <div
          className="absolute -left-3 top-0 bottom-0 w-[2px] rounded-full"
          style={{ backgroundColor: accent }}
        />
      ) : null}
      <span className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[#71717a]">
        {label}
      </span>
      <div className="flex items-baseline gap-2">
        <span
          className={`${emphasis ? 'text-[34px] font-bold' : 'text-[22px]'} font-mono tracking-tight text-[#1a1a1a]`}
        >
          {value}
        </span>
        {subValue ? (
          <span className="text-[10px] font-mono uppercase tracking-tighter text-[#71717a]">
            {subValue}
          </span>
        ) : null}
      </div>
    </div>
  )
}

const PerformanceMetric = ({ label, value, percentage }) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-baseline justify-between">
      <span className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[#1a1a1a]/90">
        {label}
      </span>
      <span className="font-mono text-[13px] font-medium text-[#1a1a1a]">{value}</span>
    </div>
    <div className="h-[1px] w-full overflow-hidden rounded-full bg-[#e4e4e7]/70">
      <div
        className="h-full bg-[#1a1a1a]/20"
        style={{ width: `${Math.max(0, Math.min(percentage, 100))}%` }}
      />
    </div>
  </div>
)

const DeviceCard = ({ label, value, Icon }) => (
  <div className="flex items-center justify-between rounded-sm border border-[#e4e4e7] bg-white p-4">
    <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-[#1a1a1a]">
      <Icon className="text-[16px]" />
      {label}
    </div>
    <div className="font-mono text-[22px] tracking-tight text-[#1a1a1a]">{value}</div>
  </div>
)

const StatusBadge = ({ status }) => {
  const tones = {
    Verified: 'border-[#5FAE8A]/20 bg-[#5FAE8A]/5 text-[#4f9974]',
    Tracked: 'border-[#DADADA]/60 bg-[#DADADA]/10 text-[#71717a]',
    Unmatched: 'border-[#dadada]/60 bg-[#dadada]/10 text-[#71717a]',
    'Bot-like': 'border-[#E07A73]/20 bg-[#E07A73]/5 text-[#E07A73]',
    Bot: 'border-[#E07A73]/20 bg-[#E07A73]/5 text-[#E07A73]',
  }
  return (
    <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-tighter ${tones[status] || tones.Tracked}`}>
      {status}
    </span>
  )
}

const IntegrityMap = ({ locations, clicks }) => {
  const [tooltip, setTooltip] = useState(null)
  const mapPath = useMemo(() => {
    const projection = geoMercator().scale(90).translate([300, 200])
    return geoPath(projection)
  }, [])
  const countryStats = useMemo(() => {
    const stats = new Map()

    clicks.forEach((click) => {
      const countryId = getCountryIdFromClick(click)
      if (!countryId) return
      const current = stats.get(countryId) || {
        clicks: 0,
        verified: 0,
        bot: 0,
        tracked: 0,
      }
      current.clicks += 1
      if (click.conversion) current.verified += 1
      if (click.isBotClick) current.bot += 1
      if (!click.conversion && !click.isBotClick) current.tracked += 1
      stats.set(countryId, current)
    })

    if (stats.size === 0) {
      locations.forEach((location) => {
        const countryId = getCountryIdFromLocation(location)
        if (!countryId) return
        const current = stats.get(countryId) || {
          clicks: 0,
          verified: 0,
          bot: 0,
          tracked: 0,
        }
        const locationClicks = Number(location.clickCount || 0)
        const locationVerified = Number(location.conversionCount || 0)
        current.clicks += locationClicks
        current.verified += locationVerified
        current.tracked += Math.max(locationClicks - locationVerified, 0)
        stats.set(countryId, current)
      })
    }

    return stats
  }, [clicks, locations])

  return (
    <div className="relative rounded-sm border border-[#d5ddd8] bg-white p-4">
      <div className="mb-4 flex flex-wrap justify-center gap-4 text-[9px] uppercase tracking-widest text-[#4b5560]">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.verified }} />
          Verified
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.bot }} />
          Bot Signals
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.unmatched }} />
          Tracked
        </div>
      </div>
      <svg
        viewBox="0 0 600 300"
        className="h-auto w-full"
        role="img"
        aria-label="Campaign traffic by world region"
        onMouseLeave={() => setTooltip(null)}
      >
        {WORLD_COUNTRIES.map((country, index) => {
          const countryId =
            country.id === undefined || country.id === null
              ? null
              : String(country.id).padStart(3, '0')
          const countryKey = countryId || `country-${index}`
          const stats = countryId ? countryStats.get(countryId) : null
          return (
            <path
              key={countryKey}
              d={mapPath(country) || ''}
              fill={stats ? COLORS.mapActive : COLORS.mapNeutral}
              stroke={stats ? '#6B7D70' : '#D5DDD8'}
              strokeWidth={stats ? '0.9' : '0.45'}
              className={stats ? 'cursor-pointer' : ''}
              onMouseEnter={(event) => {
                if (!stats) return
                setTooltip({
                  x: event.clientX,
                  y: event.clientY,
                  countryId: countryId || countryKey,
                  ...stats,
                })
              }}
              onMouseMove={(event) => {
                if (!stats) return
                setTooltip((current) =>
                  current
                    ? { ...current, x: event.clientX, y: event.clientY }
                    : current
                )
              }}
            />
          )
        })}
        {WORLD_COUNTRIES.map((country, index) => {
          const countryId =
            country.id === undefined || country.id === null
              ? null
              : String(country.id).padStart(3, '0')
          const stats = countryId ? countryStats.get(countryId) : null
          if (!stats) return null

          const [x, y] = mapPath.centroid(country)
          if (!Number.isFinite(x) || !Number.isFinite(y)) return null

          const signals = [
            { key: 'verified', value: stats.verified, fill: COLORS.verified },
            { key: 'bot', value: stats.bot, fill: COLORS.bot },
            { key: 'tracked', value: stats.tracked, fill: COLORS.unmatched },
          ].filter((signal) => signal.value > 0)
          if (signals.length === 0) return null

          const startX = x - ((signals.length - 1) * 7) / 2

          return (
            <g key={`signals-${countryId || index}`} pointerEvents="none">
              {signals.map((signal, signalIndex) => (
                <circle
                  key={signal.key}
                  cx={startX + signalIndex * 7}
                  cy={y}
                  r={4.8}
                  fill={signal.fill}
                  stroke="#ffffff"
                  strokeWidth="1.4"
                />
              ))}
            </g>
          )
        })}
      </svg>

      {tooltip ? (
        <div
          className="pointer-events-none fixed z-[100] min-w-[160px] rounded-sm border border-[#e4e4e7] bg-white p-3 shadow-xl"
          style={{ left: tooltip.x + 10, top: tooltip.y + 10 }}
        >
          <div className="flex flex-col gap-2">
            <span className="border-b border-[#e4e4e7] pb-1 text-[10px] font-semibold uppercase tracking-wider text-[#1a1a1a]">
              {getCountryLabel(tooltip.countryId)}
            </span>
            <div className="flex items-center justify-between gap-4">
              <span className="text-[9px] uppercase tracking-tighter text-[#71717a]">Verified</span>
              <span className="font-mono text-[11px] font-medium text-[#5FAE8A]">
                {formatNumber(tooltip.verified)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-[9px] uppercase tracking-tighter text-[#71717a]">Bot Signals</span>
              <span className="font-mono text-[11px] font-medium text-[#E07A73]">
                {formatNumber(tooltip.bot)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-[9px] uppercase tracking-tighter text-[#71717a]">Tracked</span>
              <span className="font-mono text-[11px] font-medium text-[#A3A3A3]">
                {formatNumber(tooltip.tracked)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-[9px] uppercase tracking-tighter text-[#71717a]">Clicks</span>
              <span className="font-mono text-[11px] font-medium text-[#1a1a1a]">
                {formatNumber(tooltip.clicks)}
              </span>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  )
}

const ChartNotice = ({ inferred }) =>
  inferred ? (
    <div className="mt-3 border border-[#e6e2d9] bg-white px-3 py-2 text-[10px] text-[#7b7a76]">
      Conversion bars are allocated from the reported campaign total because dated conversion points were not returned.
    </div>
  ) : null

const EmptyChartNotice = ({ show }) =>
  show ? (
    <div className="mt-3 border border-[#e6e2d9] bg-white px-3 py-2 text-[10px] text-[#7b7a76]">
      Conversion total is available, but the conversion trend endpoint returned no dated points.
    </div>
  ) : null

const CandlestickTrend = ({ data }) => {
  const width = 900
  const height = 320
  const margin = { top: 18, right: 24, bottom: 46, left: 44 }
  const plotWidth = width - margin.left - margin.right
  const plotHeight = height - margin.top - margin.bottom
  const maxValue = Math.max(
    1,
    ...data.map((point) => Number(point.clicks || 0)),
    ...data.map((point) => Number(point.conversions || 0) + Number(point.botSignals || 0))
  )
  const roundedMax = getNiceMax(maxValue)
  const yForValue = (value) =>
    margin.top + plotHeight - (Number(value || 0) / roundedMax) * plotHeight
  const ticks = Array.from({ length: 5 }, (_, index) => (roundedMax / 4) * index)
  const visiblePointCount = Math.max(data.length, 1)
  const step = plotWidth / visiblePointCount
  const candleWidth = Math.max(4, Math.min(34, step * 0.55))

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-[320px] w-full" role="img" aria-label="Candlestick traffic trend">
      <rect x="0" y="0" width={width} height={height} fill="transparent" />
      {ticks.map((tick, index) => {
        const y = yForValue(tick)
        return (
          <g key={`tick-${index}`}>
            <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke={COLORS.border} strokeDasharray="3 3" />
            <text x={margin.left - 10} y={y + 4} textAnchor="end" fill={COLORS.muted} fontSize="10">
              {Number.isInteger(tick) ? tick : tick.toFixed(1)}
            </text>
          </g>
        )
      })}
      <line x1={margin.left} x2={width - margin.right} y1={yForValue(0)} y2={yForValue(0)} stroke={COLORS.border} />
      {data.length === 0 ? (
        <text x={width / 2} y={height / 2} textAnchor="middle" fill={COLORS.muted} fontSize="12" letterSpacing="1.5">
          NO TREND DATA
        </text>
      ) : (
        data.map((point, index) => {
          const clicks = Number(point.clicks || 0)
          const conversions = Math.min(Number(point.conversions || 0), clicks)
          const botSignals = Math.min(
            Number(point.botSignals || 0),
            Math.max(clicks - conversions, 0)
          )
          const tracked = Math.max(clicks - conversions - botSignals, 0)
          const x = margin.left + step * index + step / 2
          const clickTop = yForValue(clicks)
          const base = yForValue(0)
          const capWidth = Math.min(candleWidth + 8, step * 0.85)
          const segments = [
            { label: 'Verified', value: conversions, fill: COLORS.verified },
            { label: 'Tracked', value: tracked, fill: COLORS.unmatched },
            { label: 'Bot Signals', value: botSignals, fill: COLORS.bot },
          ].filter((segment) => segment.value > 0)
          let stackedValue = 0

          return (
            <g key={`${point.label}-${index}`}>
              <title>{`${point.label}: ${formatNumber(conversions)} verified, ${formatNumber(tracked)} tracked, ${formatNumber(botSignals)} bot signals`}</title>
              <line x1={x} x2={x} y1={clickTop} y2={base} stroke="#D4D4D8" strokeWidth="1.5" />
              <line x1={x - capWidth / 2} x2={x + capWidth / 2} y1={clickTop} y2={clickTop} stroke="#BDBAB2" strokeWidth="2" />
              <line x1={x - capWidth / 3} x2={x + capWidth / 3} y1={base} y2={base} stroke="#DADADA" strokeWidth="1.5" />
              {segments.map((segment, segmentIndex) => {
                const yBottom = yForValue(stackedValue)
                stackedValue += segment.value
                const yTop = yForValue(stackedValue)
                const segmentHeight = Math.max(yBottom - yTop, 3)

                return (
                  <rect
                    key={segment.label}
                    x={x - candleWidth / 2}
                    y={yTop}
                    width={candleWidth}
                    height={segmentHeight}
                    fill={segment.fill}
                    rx={segmentIndex === segments.length - 1 ? '1.5' : '0'}
                  />
                )
              })}
              <text x={x} y={height - 20} textAnchor="middle" fill={COLORS.muted} fontSize="10">
                {point.label}
              </text>
            </g>
          )
        })
      )}
    </svg>
  )
}

const CampaignOverview = () => {
  const navigate = useNavigate()
  const { campaignId } = useParams()
  const defaultDateRange = useMemo(() => getLast30DayRange(), [])
  const [dateRange, setDateRange] = useState(defaultDateRange)
  const [chartData, setChartData] = useState([])
  const [rawClicks, setRawClicks] = useState([])
  const [clickCount, setClickCount] = useState(0)
  const [lastDate, setLastDate] = useState()
  const [locationView, setLocationView] = useState('map')
  const [ledgerOpen, setLedgerOpen] = useState(false)
  const [isMSightOpen, setIsMSightOpen] = useState(false)

  useEffect(() => {
    setDateRange(defaultDateRange)
  }, [campaignId, defaultDateRange])

  const campaignDetails = useQuery(MY_CAMPAIGNS_DETAILS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { campaignId },
  })

  const [loadMoreClicks, { data: moreClicksData, loading: loadingMoreClicks }] = useLazyQuery(
    MY_CAMPAIGNS_CLICKS_QUERY,
    {
      fetchPolicy: 'network-only',
    }
  )

  const [clickStats] = useLazyQuery(MY_CAMPAIGNS_CLICK_STATS_QUERY, {
    fetchPolicy: 'no-cache',
  })
  const [conversionStats] = useLazyQuery(MY_CAMPAIGNS_CONVERSION_STATS_QUERY, {
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    const clicks = campaignDetails.data?.myCampaignDetails?.clicks?.clickDatas || []
    setRawClicks(normalizeClicks(clicks))
    setClickCount(campaignDetails.data?.myCampaignDetails?.clicks?.clickCount || 0)
    if (clicks.length > 0) {
      setLastDate(clicks[clicks.length - 1].createdAt)
    }
  }, [campaignDetails.data])

  useEffect(() => {
    const clicks = moreClicksData?.myCampaignClicks?.clickDatas || []
    if (clicks.length === 0) return
    setRawClicks((prev) => prev.concat(normalizeClicks(clicks)))
    setClickCount(moreClicksData?.myCampaignClicks?.clickCount || 0)
    setLastDate(clicks[clicks.length - 1].createdAt)
  }, [moreClicksData])

  useEffect(() => {
    let isCurrentRequest = true

    Promise.all([
      clickStats({ variables: { campaignId, groupby: TREND_GROUP_BY } }),
      conversionStats({ variables: { campaignId, groupby: TREND_GROUP_BY } }),
    ]).then(([clickRes, conversionRes]) => {
      if (!isCurrentRequest) return

      setChartData(
        mergeStats(
          clickRes?.data?.myCampaignClickStats?.stats || [],
          conversionRes?.data?.myCampaignConversionStats?.stats || []
        )
      )
    })

    return () => {
      isCurrentRequest = false
    }
  }, [campaignId, clickStats, conversionStats])

  const details = campaignDetails.data?.myCampaignDetails
  const campaign = details?.trackingCampaignData
  const cveEvents = normalizeCves(details?.cves || [])
  const locations = details?.chartDatas?.topLocations || []

  const totalClicks = Number(campaign?.clicks || 0)
  const uniqueClicks = Number(campaign?.uniqueClicks || 0)
  const duplicateConversions = Number(campaign?.duplicateConversions || 0)
  const botClicks = Number(campaign?.botClicks || 0)
  const conversions = Number(campaign?.conversions || 0)
  const conversionMetricTotal = conversions
  const clickDenominator = Math.max(totalClicks, 1)
  const uniqueShare = (uniqueClicks / clickDenominator) * 100
  const conversionMetricShare = (conversionMetricTotal / clickDenominator) * 100
  const botShare = (botClicks / clickDenominator) * 100

  const desktopCount = Number(campaign?.desktopClicks || 0)
  const mobileCount = Math.max(totalClicks - desktopCount, 0)
  const totalDeviceCount = desktopCount + mobileCount
  const desktopShare = totalDeviceCount
    ? `${Math.round((desktopCount / totalDeviceCount) * 100)}%`
    : '0%'
  const mobileShare = totalDeviceCount
    ? `${Math.round((mobileCount / totalDeviceCount) * 100)}%`
    : '0%'
  const cpa = conversions
    ? Number(campaign?.campaignBudget || 0) / conversions
    : 0

  const availableDateRange = useMemo(() => getDateRangeBounds(chartData), [chartData])
  const selectedRangeStart = dateRange.start
  const selectedRangeEnd = dateRange.end
  const isCustomDateRange =
    selectedRangeStart !== defaultDateRange.start ||
    selectedRangeEnd !== defaultDateRange.end
  const dateRangeLabel = isCustomDateRange ? 'Custom date range' : 'Last 30 days'
  // The graph uses this filtered data, so changing the From/To controls updates
  // the displayed trend immediately through dateRange state.
  const filteredChartData = useMemo(
    () =>
      filterStatsByDateRange(chartData, {
        start: selectedRangeStart,
        end: selectedRangeEnd,
      }),
    [chartData, selectedRangeStart, selectedRangeEnd]
  )
  const updateDateRange = (field, value) => {
    setDateRange((current) => {
      const next = { ...current, [field]: value }

      if (next.start && next.end && next.start > next.end) {
        if (field === 'start') {
          next.end = next.start
        } else {
          next.start = next.end
        }
      }

      return next
    })
  }

  const baseTrend = filteredChartData.map((point) => {
    return {
      label: formatShortDate(point.position),
      clicks: point.clickCount || 0,
      conversions: point.conversionCount || 0,
    }
  })
  const hasConversionTrend = baseTrend.some((point) => point.conversions > 0)
  const inferredConversionTrend = conversions > 0 && baseTrend.length > 0 && !hasConversionTrend
  const trendWithConversions = inferredConversionTrend
    ? distributeConversionsAcrossTrend(baseTrend, conversions)
    : baseTrend
  const trend = distributeBotSignalsAcrossTrend(trendWithConversions, botClicks)

  const fallbackClickEvents = rawClicks.map((click) => ({
    ...click,
    status: click.isBotClick ? 'Bot-like' : click.conversion ? 'Verified' : 'Tracked',
  }))
  const events = (cveEvents.length > 0 ? cveEvents : fallbackClickEvents).slice(0, 6)
  const auditRows = cveEvents.length > 0 ? cveEvents : fallbackClickEvents

  const referrerBreakdown = useMemo(() => {
    const map = new Map()
    rawClicks.forEach((click) => {
      const key = click.referrerLabel
      const current = map.get(key) || { label: key, clicks: 0, conversions: 0 }
      current.clicks += 1
      if (click.conversion) current.conversions += 1
      map.set(key, current)
    })
    return Array.from(map.values())
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 3)
  }, [rawClicks])

  const exportCsv = () => {
    if (!details) return
    do_download_string({
      content: [
        ['Campaign Name', 'Created At', 'Clicks', 'Unique Clicks', 'Bot Clicks', 'Conversions', 'CPA'],
        [campaign.campaignName, formatDate(campaign.createdAt), campaign.clicks, campaign.uniqueClicks, campaign.botClicks, conversions, cpa],
      ]
        .concat([[], ['Location', 'Clicks', 'Conversions']])
        .concat(locations.map((location) => [location.city, location.clickCount, location.conversionCount])),
      filename: 'campaign_overview.csv',
    })
  }

  return (
    <div
      id="outer-container"
      style={{
        height: '100%',
        fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
      }}
    >
      <MenuWrap className="sm:hidden">
        <SideMenu isAuthorized={true} />
      </MenuWrap>
      <div id="page-wrap">
        <div className="flex" style={{ backgroundColor: COLORS.bg }}>
          <SideMenu isAuthorized={true} className="hidden sm:block" />
          <div className="h-screen w-full overflow-y-auto">
            <SimpleHeader />
            {campaignDetails.loading ? (
              <div className="loader-wrapper">
                <div className="flex items-center justify-center">
                  <div className="grid gap-10">
                    <div className="loader m-auto"></div>
                    <div className="text-center font-unbounded">
                      Loading campaign analytics...
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-4 pb-10 pt-6 md:px-8 xl:px-10">
                <div className="mx-auto max-w-[1500px]">
                  <header className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-[#111111] text-[10px] font-semibold text-white">
                          M
                        </div>
                        <h1 className="text-[24px] text-[#171717]">
                          {campaign?.campaignName}
                        </h1>
                      </div>
                      <div className="mt-2 flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-[#71717a]">
                        <span className="flex items-center gap-1.5">
                          <FiGlobe className="text-[12px]" />
                          {campaign?.platform || 'Platform'}
                        </span>
                        <span className="h-[3px] w-[3px] rounded-full bg-[#b9b7b1]" />
                        <span className="flex items-center gap-1">
                          {dateRangeLabel}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 lg:justify-end">
                      <button
                        onClick={() => navigate('/first-tracker')}
                        className="bg-[#111111] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white"
                      >
                        + New M-Link
                      </button>
                      <button
                        onClick={exportCsv}
                        hidden
                        className="hidden items-center gap-2 border border-[#d8d5ce] bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#171717]"
                        aria-hidden="true"
                      >
                        <AiOutlineDownload />
                        Generate Evidence Report
                      </button>
                      <button
                        onClick={() => navigator.clipboard?.writeText(window.location.href)}
                        hidden
                        className="hidden items-center gap-2 border border-[#d8d5ce] bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#171717]"
                        aria-hidden="true"
                      >
                        <HiOutlineShare />
                        Share
                      </button>
                    </div>
                  </header>

                  <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_340px]">
                    <div className="space-y-10">
                      <section>
                        <SectionHeader title="Verification Summary" subtitle="Validated conversion integrity audit" />
                        <div className="rounded-sm border border-[#e4e4e7] bg-white p-8 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
                          <div className="grid gap-8 md:grid-cols-3">
                            <StatItem label="Verified" value={formatNumber(conversions)} subValue="Truth" emphasis colorType="verified" />
                            <StatItem label="Bot Signals" value={formatNumber(botClicks)} colorType="bot" />
                            <StatItem label="Duplicates" value={formatNumber(duplicateConversions)} colorType="duplicate" />
                          </div>
                        </div>
                      </section>

                      <section>
                        <SectionHeader
                          title="Verification Trend"
                          subtitle="Temporal integrity analysis"
                          action={
                            <fieldset
                              className="flex flex-wrap items-center gap-2 rounded-sm border border-[#e4e4e7] bg-white px-2 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
                            >
                              <FiCalendar className="text-[13px] text-[#71717a]" />
                              <label className="flex items-center gap-1.5">
                                <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#71717a]">
                                  From
                                </span>
                                <input
                                  aria-label="Trend start date"
                                  type="date"
                                  value={selectedRangeStart}
                                  min={availableDateRange.start}
                                  max={selectedRangeEnd || defaultDateRange.end}
                                  onInput={(event) => updateDateRange('start', event.currentTarget.value)}
                                  onChange={(event) => updateDateRange('start', event.target.value)}
                                  className="h-7 w-[124px] border border-[#e4e4e7] bg-[#f9f9f8] px-2 font-mono text-[10px] uppercase tracking-wide text-[#1a1a1a] outline-none"
                                />
                              </label>
                              <span className="text-[10px] text-[#a1a1aa]">/</span>
                              <label className="flex items-center gap-1.5">
                                <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#71717a]">
                                  To
                                </span>
                                <input
                                  aria-label="Trend end date"
                                  type="date"
                                  value={selectedRangeEnd}
                                  min={selectedRangeStart || availableDateRange.start}
                                  max={defaultDateRange.end}
                                  onInput={(event) => updateDateRange('end', event.currentTarget.value)}
                                  onChange={(event) => updateDateRange('end', event.target.value)}
                                  className="h-7 w-[124px] border border-[#e4e4e7] bg-[#f9f9f8] px-2 font-mono text-[10px] uppercase tracking-wide text-[#1a1a1a] outline-none"
                                />
                              </label>
                            </fieldset>
                          }
                        />
                        <div className="h-[320px]">
                          <CandlestickTrend data={trend} />
                        </div>
                        <ChartNotice inferred={inferredConversionTrend} />
                        <EmptyChartNotice show={conversions > 0 && chartData.length === 0} />
                        <div className="mt-8 flex flex-wrap justify-end gap-8 text-[12px] font-semibold uppercase tracking-[0.15em] text-[#1a1a1a]/90">
                          <div className="flex items-center gap-2.5">
                            <span className="h-4 w-4 rounded-[2px]" style={{ backgroundColor: COLORS.verified }} />
                            Verified
                          </div>
                          <div className="flex items-center gap-2.5">
                            <span className="h-4 w-4 rounded-[2px]" style={{ backgroundColor: COLORS.unmatched }} />
                            Tracked
                          </div>
                          <div className="flex items-center gap-2.5">
                            <span className="h-4 w-4 rounded-[2px]" style={{ backgroundColor: COLORS.bot }} />
                            Bot Signals
                          </div>
                        </div>
                      </section>

                      <section>
                        <SectionHeader title="Verification Events" subtitle="Raw evidence stream" />
                        <div className="overflow-hidden rounded-sm border border-[#e4e4e7] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
                          <div className="overflow-x-auto">
                            <table className="min-w-full">
                              <thead>
                                <tr className="border-b border-[#e4e4e7] bg-[#f9f9f8]/50 text-left text-[12px] font-semibold uppercase tracking-[0.15em] text-[#71717a]">
                                  <th className="px-6 py-3">Timestamp</th>
                                  <th className="px-6 py-3">Referrer</th>
                                  <th className="px-6 py-3">User Agent</th>
                                  <th className="px-6 py-3">IP / Location</th>
                                  <th className="px-6 py-3 text-right">Classification</th>
                                </tr>
                              </thead>
                              <tbody className="font-mono text-[12px]">
                                {events.map((event) => (
                                  <tr key={event.id} className="border-b border-[#e4e4e7] transition-colors last:border-b-0 hover:bg-[#f9f9f8]/60">
                                    <td className="px-6 py-4 text-[#71717a]">{event.createdAtLabel}</td>
                                    <td className="px-6 py-4 text-[#1a1a1a]">{event.referrerLabel}</td>
                                    <td className="px-6 py-4 text-[#71717a]" title={event.userAgentTooltip}>{event.userAgentLabel}</td>
                                    <td className="px-6 py-4 text-[#1a1a1a]"><div>{event.ip}</div><div className="mt-1 text-[9px] opacity-80">{event.locationLabel}</div></td>
                                    <td className="px-6 py-4 text-right"><StatusBadge status={event.status} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex items-center justify-between border-t border-[#e4e4e7] bg-[#f9f9f8]/20 px-6 py-3 font-mono text-[10px] text-[#71717a]">
                            <span>Showing {events.length} of {formatNumber(cveEvents.length || clickCount)} events</span>
                            <button onClick={() => setLedgerOpen((open) => !open)} className="font-medium uppercase tracking-widest text-[#1a1a1a] hover:underline">View Full Audit Log</button>
                          </div>
                        </div>
                      </section>

                      {ledgerOpen ? (
                        <section>
                          <SectionHeader title="Audit Log" subtitle="Full raw conversion ledger" />
                          <div className="border border-[#e6e2d9] bg-[#fcfbf8]">
                            <div className="overflow-x-auto">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="border-b border-[#e6e2d9] text-left text-[9px] font-semibold uppercase tracking-[0.18em] text-[#8b8a86]">
                                    <th className="px-4 py-3">Timestamp</th>
                                    <th className="px-4 py-3">IP</th>
                                    <th className="px-4 py-3">Device</th>
                                    <th className="px-4 py-3">Referrer</th>
                                    <th className="px-4 py-3">Location</th>
                                    <th className="px-4 py-3 text-right">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {auditRows.map((click) => (
                                    <tr key={click.id} className="border-b border-[#e6e2d9] last:border-b-0">
                                      <td className="px-4 py-4 text-[11px] text-[#7b7a76]">{click.createdAtLabel}</td>
                                      <td className="px-4 py-4 text-[11px] text-[#171717]">{click.ip}</td>
                                      <td className="px-4 py-4 text-[11px] text-[#7b7a76]" title={click.userAgentTooltip}>{click.deviceLabel}</td>
                                      <td className="px-4 py-4 text-[11px] text-[#171717]">{click.referrerLabel}</td>
                                      <td className="px-4 py-4 text-[11px] text-[#171717]">{click.locationLabel}</td>
                                      <td className="px-4 py-4 text-right text-[10px] uppercase tracking-[0.18em]"><StatusBadge status={click.status || (click.isBotClick ? 'Bot-like' : 'Verified')} /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="flex flex-col items-center justify-between gap-4 border-t border-[#e6e2d9] px-4 py-4 md:flex-row">
                              <div className="text-[10px] text-[#8b8a86]">Showing {auditRows.length} of {formatNumber(cveEvents.length || clickCount)}</div>
                              {cveEvents.length === 0 && clickCount > rawClicks.length ? (
                                <button
                                  onClick={() => lastDate && loadMoreClicks({ variables: { campaignId, createdAt: lastDate } })}
                                  disabled={loadingMoreClicks}
                                  className="border border-[#171717] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#171717] disabled:opacity-60"
                                >
                                  {loadingMoreClicks ? 'Loading...' : 'Load More'}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </section>
                      ) : null}
                    </div>

                    <aside className="space-y-8">
                      <section>
                        <SectionHeader title="Performance Context" subtitle="Reported platform metrics" />
                        <div className="flex flex-col gap-6 px-2">
                          <PerformanceMetric label="Clicks" value={formatNumber(totalClicks)} percentage={100} />
                          <PerformanceMetric label="Unique Clicks" value={formatNumber(uniqueClicks)} percentage={uniqueShare} />
                          <PerformanceMetric label="Conversions" value={formatNumber(conversionMetricTotal)} percentage={conversionMetricShare} />
                          <PerformanceMetric label="Bot Signals" value={formatNumber(botClicks)} percentage={botShare} />
                          <PerformanceMetric label="CPA" value={formatCurrency(cpa)} percentage={conversions > 0 ? 60 : 0} />
                        </div>
                      </section>

                      <section>
                        <SectionHeader title="By Device" />
                        <div className="space-y-3">
                          <DeviceCard label="Desktop" value={desktopShare} Icon={FiMonitor} />
                          <DeviceCard label="Mobile / Other" value={mobileShare} Icon={FiSmartphone} />
                        </div>
                      </section>

                      <section>
                        <SectionHeader
                          title="By Location"
                          action={
                            <div className="flex rounded-sm border border-[#e4e4e7] bg-[#f9f9f8] p-0.5">
                              {['map', 'table'].map((value) => (
                                <button
                                  key={value}
                                  onClick={() => setLocationView(value)}
                                  className="rounded-sm px-3 py-1 text-[9px] font-semibold uppercase tracking-widest transition-colors"
                                  style={{
                                    backgroundColor: locationView === value ? '#ffffff' : 'transparent',
                                    color: locationView === value ? COLORS.active : COLORS.muted,
                                    boxShadow: locationView === value ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                                  }}
                                >
                                  {value}
                                </button>
                              ))}
                            </div>
                          }
                        />
                        {locationView === 'map' ? (
                          <IntegrityMap locations={locations} clicks={rawClicks} />
                        ) : (
                          <div className="rounded-sm border border-[#e4e4e7] bg-white">
                            <div className="grid grid-cols-[minmax(0,1fr)_70px_70px] border-b border-[#e4e4e7] bg-[#f9f9f8]/50 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-[#71717a]">
                              <span>City</span>
                              <span className="text-right">Clicks</span>
                              <span className="text-right">Conv.</span>
                            </div>
                            {locations.slice(0, 4).map((location, index) => (
                              <div key={`${location.city}-${index}`} className="grid grid-cols-[minmax(0,1fr)_70px_70px] border-b border-[#e4e4e7] px-4 py-3 font-mono text-[12px] text-[#1a1a1a] last:border-b-0">
                                <span>{location.city || 'Unknown city'}</span>
                                <span className="text-right">{formatNumber(location.clickCount)}</span>
                                <span className="text-right">{formatNumber(location.conversionCount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </section>

                      <section>
                        <SectionHeader title="By Referrer" />
                        <div className="rounded-sm border border-[#e4e4e7] bg-white">
                          <div className="grid grid-cols-[minmax(0,1fr)_70px_70px] border-b border-[#e4e4e7] bg-[#f9f9f8]/50 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-[#71717a]">
                            <span>Source</span>
                            <span className="text-right">Clicks</span>
                            <span className="text-right">Conv.</span>
                          </div>
                          {referrerBreakdown.map((referrer) => (
                            <div key={referrer.label} className="grid grid-cols-[minmax(0,1fr)_70px_70px] border-b border-[#e4e4e7] px-4 py-3 font-mono text-[12px] text-[#1a1a1a] last:border-b-0">
                              <span className="truncate">{referrer.label}</span>
                              <span className="text-right">{formatNumber(referrer.clicks)}</span>
                              <span className="text-right">{formatNumber(referrer.conversions)}</span>
                            </div>
                          ))}
                        </div>
                      </section>

                    </aside>
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={() => setIsMSightOpen(true)}
              hidden
              className="hidden mx-auto mt-6 items-center gap-2.5 rounded-sm bg-[#1a1a1a] px-5 py-3 text-white shadow-2xl transition-colors hover:bg-[#1a1a1a]/90 sm:fixed sm:bottom-8 sm:right-8 sm:z-40 sm:mt-0"
              aria-hidden="true"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white">
                <img src={Robot} alt="" className="h-4 w-4 object-cover opacity-80" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em]">Ask M-Sight</span>
            </button>
            {isMSightOpen ? (
              <>
                <div
                  className="fixed inset-0 z-50 bg-[#1a1a1a]/10 backdrop-blur-[2px]"
                  onClick={() => setIsMSightOpen(false)}
                />
                <aside className="fixed bottom-0 right-0 top-0 z-50 flex w-[400px] max-w-[calc(100vw-24px)] flex-col border-l border-[#e4e4e7] bg-white shadow-2xl">
                  <div className="flex items-center justify-between border-b border-[#e4e4e7] p-6">
                    <div className="flex items-center gap-3">
                      <img src={Robot} alt="M-Sight" className="h-5 w-5 object-cover" />
                      <h2 className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1a1a]">M-Sight</h2>
                    </div>
                    <button
                      onClick={() => setIsMSightOpen(false)}
                      className="rounded-sm p-2 text-[#71717a] transition-colors hover:bg-[#f9f9f8] hover:text-[#1a1a1a]"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                  <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
                    <div className="flex flex-col gap-2">
                      <p className="text-[14px] font-light tracking-wide text-[#71717a]">Ask about this campaign</p>
                      <p className="font-mono text-[11px] leading-relaxed text-[#71717a]">
                        M-Sight provides a context-aware interpretation layer for campaign verification, anomalies, and event classifications.
                      </p>
                    </div>
                    <div className="flex flex-col gap-5 rounded-sm border border-[#e4e4e7] bg-[#f9f9f8]/30 p-5">
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#de7c72]">Signal Summary</span>
                        <h3 className="text-[13px] font-semibold uppercase tracking-tight text-[#1a1a1a]">Campaign Integrity Snapshot</h3>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[#71717a]">Primary Drivers</span>
                        <ul className="flex flex-col gap-1.5">
                          {[
                            `${formatNumber(conversions)} verified conversion events`,
                            `${formatNumber(botClicks)} bot-like click signals`,
                            `${formatNumber(uniqueClicks)} unique clicks in current window`,
                          ].map((driver) => (
                            <li key={driver} className="flex items-center gap-2 font-mono text-[11px] text-[#1a1a1a]/80">
                              <span className="h-1 w-1 rounded-full bg-[#71717a]" />
                              {driver}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="border-t border-[#e4e4e7] pt-4">
                        <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.15em] text-[#71717a]">Recommendation</span>
                        <div className="flex items-start gap-3 rounded-sm border border-[#e4e4e7] bg-white p-3">
                          <FiAlertCircle size={14} className="mt-0.5 text-[#1a1a1a]" />
                          <p className="text-[11px] font-medium leading-snug text-[#1a1a1a]">
                            Review bot-like events alongside referrer and location concentration before optimizing spend.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-[#e4e4e7] bg-[#f9f9f8]/20 p-6">
                    <div className="relative">
                      <textarea
                        placeholder="Type your query..."
                        className="min-h-[100px] w-full resize-none rounded-sm border border-[#e4e4e7] bg-white p-4 pr-12 font-mono text-[13px] placeholder:text-[#71717a]/50 focus:border-[#1a1a1a]/30 focus:outline-none"
                      />
                      <button className="absolute bottom-4 right-4 rounded-sm bg-[#1a1a1a] p-2 text-white transition-colors hover:bg-[#1a1a1a]/90">
                        <FiSend size={14} />
                      </button>
                    </div>
                    <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-tighter text-[#71717a]">
                      Audit Intelligence Layer - v1.0.4
                    </p>
                  </div>
                </aside>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CampaignOverview
